import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../types/navigationParams";
import { Divider, Surface, Text, IconButton } from "react-native-paper";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { HeaderBackButton } from "@react-navigation/elements";
import DynamicForm from "../../components/DynamicForm";
import { update } from "../../services/firestoreServices";
import { deleteEmployee } from "../../services/employeeServices";
import { updateTimestampAction } from "../../redux/slices/authSlice";
import { USERS_COLLECTION } from "../../constants/collections";
import { useTranslation } from "react-i18next";
import { Field } from "../../config/companies/config";
import { alert as showAlert } from "../../utils/alert";

type DetailsScreenProps = NativeStackScreenProps<
  RootStackParamList,
  "EmployeeDetailsScreen"
>;

const collection = USERS_COLLECTION;

const formatLastLogin = (value: any) => {
  if (!value) return "-";

  const date =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

const getPlatformLastLogin = (
  employee: Record<string, any>,
  platform: "web" | "ios" | "android",
) => {
  const byPlatform = employee.lastLoginByPlatform?.[platform];
  if (byPlatform) return byPlatform;

  if (employee.lastLoginPlatform === platform && employee.lastLoginAt) {
    return employee.lastLoginAt;
  }

  return null;
};

export const EmployeeDetailsScreen = (props: DetailsScreenProps) => {
  const { route, navigation } = props;
  const collections = useAppSelector((state) => state.auth.config.database);
  const config = useAppSelector((state) => state.auth.config);
  const currentUser = useAppSelector((state) => state.auth.user);
  const userRole = useAppSelector((state) => state.auth.user.role);
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const usersCollection = collections.find((c) => c.name === collection);
  const employeeId = route.params.employee.id || route.params.employee.uid;
  const isEditingSelf = employeeId === currentUser.uid;

  const preparedEmployeeData = useMemo(() => {
    const rawBirthDate = route.params.employee.birthDate;
    const parsedBirthDate =
      typeof rawBirthDate === "string" && rawBirthDate
        ? new Date(`${rawBirthDate}T00:00:00`)
        : (rawBirthDate ?? null);

    return {
      ...route.params.employee,
      birthDate: parsedBirthDate,
    };
  }, [route.params.employee]);

  const employeeFields = useMemo(() => {
    const fields = [...(usersCollection?.fields || [])];

    if (!fields.some((field) => field.name === "birthDate")) {
      fields.push({
        name: "birthDate",
        type: "datePicker",
        label: "Birthday",
        list: config.roles,
        form: config.roles,
        board: [],
        required: false,
      } as Field);
    }

    if (!isEditingSelf) {
      return fields;
    }

    // Prevent self role reassignment from the form.
    return fields.filter((field) => field.name !== "role");
  }, [config.roles, isEditingSelf, usersCollection?.fields]);

  const onSubmit = async (data: any) => {
    const normalizedData = {
      ...data,
      birthDate:
        data.birthDate instanceof Date
          ? data.birthDate.toISOString().split("T")[0]
          : data.birthDate || null,
    };

    await update(collection, employeeId, normalizedData);
    dispatch(updateTimestampAction());
    navigation.goBack();
  };

  const handleDeleteEmployee = () => {
    showAlert(
      t("Delete Employee"),
      t(
        "Are you sure you want to delete this employee? This action cannot be undone.",
      ),
      [
        {
          text: t("Cancel"),
          style: "cancel",
        },
        {
          text: t("Delete"),
          style: "destructive",
          onPress: () => {
            void confirmDelete();
          },
        },
      ],
    );
  };

  const confirmDelete = async () => {
    try {
      await deleteEmployee(employeeId);
      dispatch(updateTimestampAction());
      navigation.goBack();
    } catch (error) {
      console.error("Failed to delete employee:", error);
      showAlert(
        t("Error"),
        t("Failed to delete employee. Please try again."),
        [{ text: t("OK") }],
      );
    }
  };

  return (
    <>
      <View style={styles.overlay}>
        <Surface style={styles.modalContent}>
          <View style={styles.headerRow}>
            <HeaderBackButton
              onPress={() => navigation.goBack()}
              style={{ margin: -5 }}
            />
            {config.employeesEditableBy?.includes(userRole) && (
              <IconButton
                icon="delete-outline"
                iconColor="#e74c3c"
                onPress={handleDeleteEmployee}
                style={{ margin: -5 }}
              />
            )}
          </View>
          <DynamicForm
            existingData={preparedEmployeeData}
            fields={employeeFields}
            handleSubmit={onSubmit}
            collectionName={collection}
            collection={usersCollection}
          />

          <View style={styles.lastLoginSection}>
            <View style={styles.lastLoginHeader}>
              <Text
                variant="bodySmall"
                style={styles.lastLoginTitle}
              >
                {t("Last login by platform")}
              </Text>
              <Text variant="bodySmall" style={styles.versionValue}>
                {preparedEmployeeData.appVersion
                  ? `v${preparedEmployeeData.appVersion}`
                  : "-"}
              </Text>
            </View>
            <Divider style={styles.lastLoginDivider} />
            <View style={styles.lastLoginRow}>
              <Text variant="bodySmall" style={styles.lastLoginLabel}>
                Web:
              </Text>
              <Text variant="bodySmall" style={styles.lastLoginValue}>
                {formatLastLogin(
                  getPlatformLastLogin(preparedEmployeeData, "web"),
                )}
              </Text>
            </View>
            <View style={styles.lastLoginRow}>
              <Text variant="bodySmall" style={styles.lastLoginLabel}>
                iOS:
              </Text>
              <Text variant="bodySmall" style={styles.lastLoginValue}>
                {formatLastLogin(
                  getPlatformLastLogin(preparedEmployeeData, "ios"),
                )}
              </Text>
            </View>
            <View style={styles.lastLoginRow}>
              <Text variant="bodySmall" style={styles.lastLoginLabel}>
                Android:
              </Text>
              <Text variant="bodySmall" style={styles.lastLoginValue}>
                {formatLastLogin(
                  getPlatformLastLogin(preparedEmployeeData, "android"),
                )}
              </Text>
            </View>
          </View>
        </Surface>
      </View>

    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    width: "70%",
    padding: 16,
    borderRadius: 8,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  lastLoginSection: {
    marginTop: 16,
  },
  lastLoginHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lastLoginDivider: {
    marginVertical: 8,
  },
  lastLoginTitle: {
    color: "#64748b",
    fontWeight: "600",
  },
  lastLoginRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 2,
  },
  lastLoginLabel: {
    color: "#475569",
    fontWeight: "500",
  },
  lastLoginValue: {
    color: "#0f172a",
  },
  versionValue: {
    color: "#64748b",
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
});
